import { afterNextRender, Component, computed, inject, input, signal } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NgbAccordionModule } from '@ng-bootstrap/ng-bootstrap';
import { Faqs } from '../signup.types';

@Component({
  selector: 'app-faqs',
  imports: [
    NgbAccordionModule
  ],
  templateUrl: './faqs.component.html',
  styleUrl: './faqs.component.scss'
})
export class FaqsComponent {

  //#region Services

  private sanitizer = inject(DomSanitizer);

  //#endregion

  //#region I/O

  step = input.required<number>();

  //#endregion

  //#region Properties

  faqs = signal<Faqs[]>([]);

  faqsInView = computed(() => this.faqs().find(x => x.step === this.step())?.data ?? []);

  //#endregion

  //#region Constructors

  constructor() {
    afterNextRender(() => {
      this.faqs.set([
        {
          step: 0,
          data: [
            {
              question: `Why are there specific days for ordering and delivery?`,
              answer: this.#sanitizeHtml(`
            <p>
              We work with small, local producers and our food is fresh! During the week we talk to our farmers to find out what is available for the next week. On Friday we post their offerings to online market and you can add items to your delivery from Friday morning until 7am the day before your delivery.
            <p>
            <p>
              In an attempt to decrease the carbon footprint of our deliveries as well as keep our costs down, we only deliver to certain neighborhoods on certain days of the week.
            </p>
            `)
            },
            {
              question: `What if I am not home during the delivery?`,
              answer: this.#sanitizeHtml(`
            <p>
              If you live somewhere where packages can not be left, please email us at <a href="mailto: info@phillyfoodworks.com">info@phillyfoodworks.com</a> and we will do our best to find a delivery window when you are home.
            </p>
            <p>
              If you packages can be left at your home, but you might not be home during the delivery please leave out a cooler and our driver will place the contents of your order in your cooler. Be sure to let us know on your driver notes. We have a small dedicated team of drivers and it wll likely be the same driver delivering each week.
            </p>
            `)
            },
            {
              question: `Do I have to get a subscription? Do I have to log on each week and add items to my order?`,
              answer: `
            <p>
              We prioritized flexibility in our ordering system, so you can do what you want. If you are interested in our farm shares check out the farm share section of our website! If you prefer to add individual items to your basket, you do not need to subscribe to a box. Of course, you can always do both!
            </p>
            `
            },
          ]
        },
        {
          step: 1,
          data: [
            {
              question: `What kinds of communications will you send me?`,
              answer: this.#sanitizeHtml(`
             <p>
              Each week we will send an email letting you know what is new and exciting on the market. Since we work with over 200 local producers and available changes frequently there are always new items coming into season. We will also send information about the food and the people who make it. This could be recipes, storage information, producer stories, and the ways in which we are working together to build a local food system. We’ll also send you tips in tricks for using your Philly Foodworks account, including how to save money while doing more to support our local producers!
             </p>
            `)
            },
          ]
        },
        {
          step: 2,
          data: [
            {
              question: `Will you charge my credit card?`,
              answer: `
            <p>
              Your credit card will only be charged if you place an order with us.
            </p>
            `
            },
            {
              question: `Can I cancel my Philly Foodworks account any time?`,
              answer: `
            <p>
              Yup! If at any point you decide you do not want to order with us, you can send an email to <a href="mailto: info@phillyfoodworks.com">info@phillyfoodworks.com</a> and we will cancel your account. If you decide you just want to take a break, you can keep your account open with us for as long as you want and we will not bill you.
            </p>
            `
            },
            {
              question: `Why should I choose to order my food with Philly Foodworks and support your business?`,
              answer: `
            <p>
              We started this business over a decade ago. We are just like you. We live here. We want a local food system for our region. We want a sustainable ecosystem where people and the earth are treated fairly. We want to enjoy the best food possible with our friends and families. We work day and night to make this possible.
            </p>
            <p>
              If you choose to shop with us, you will get access to the largest selection of local food in the tri-state through a convenient market and dependable community business. We guarantee you will support local producers and enjoy the food you eat or your money back!
            <p/>
            `
            }
          ]
        }
      ]);
    });
  }

  //#endregion

  //#region Methods

  #sanitizeHtml(html: string): SafeHtml {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return this.sanitizer.bypassSecurityTrustHtml(doc.body.innerHTML);
  }

  //#endregion
}
